import React from "react";
import { X } from "lucide-react";
import { useLang } from "@/i18n";
import { ProgereTwoToneTitle } from "@/progere-design-system/ProgereTwoToneTitle";

export function PageHeader({ title, subtitle, children }) {
  const { t } = useLang();
  const tt = String(t(title));
  const words = tt.split(" ");
  const head = words.slice(0, -1).join(" ");
  const last = words[words.length - 1];
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 pg-fade-up">
      <div>
        <ProgereTwoToneTitle firstPart={head} secondPart={last} firstColor="white" secondColor="gold" size="lg" />
        {subtitle && <p className="text-gray-500 text-sm mt-1">{t(subtitle)}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return <div className={`pg-card bg-[#141414] border border-white/5 rounded-xl ${className}`} {...props}>{children}</div>;
}

const TONE = {
  green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  gold: "bg-[#F2BC2E]/10 text-[#F2BC2E] border-[#F2BC2E]/20",
  red: "bg-[#F10D19]/10 text-[#F10D19] border-[#F10D19]/20",
  blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  gray: "bg-white/5 text-gray-400 border-white/10",
  purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
};

export function Badge({ tone = "gray", children }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${TONE[tone] || TONE.gray}`}>{children}</span>;
}

export function GoldButton({ children, className = "", ...props }) {
  return <button className={`btn-gold rounded-full px-4 py-2 text-sm flex items-center gap-2 ${className}`} {...props}>{children}</button>;
}

export function GhostButton({ children, className = "", ...props }) {
  return <button className={`rounded-full border border-white/15 bg-transparent text-white text-sm px-4 py-2 flex items-center gap-2 hover:bg-white/5 transition-colors ${className}`} {...props}>{children}</button>;
}

export function Modal({ open, onClose, title, children }) {
  const { t } = useLang();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 backdrop-blur-md bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#141414] border border-white/10 rounded-2xl p-6 pg-fade-up max-h-[90vh] overflow-y-auto pg-scroll" data-testid="modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{t(title)}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors" data-testid="modal-close"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Input({ label, type = "text", value, onChange, testid }) {
  const { t } = useLang();
  return (
    <div>
      {label && <label className="block text-xs font-semibold text-gray-400 mb-1.5">{t(label)}</label>}
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0f0f0f] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-[#F2BC2E] focus:outline-none transition-colors" />
    </div>
  );
}

export function Textarea({ label, value, onChange, testid }) {
  const { t } = useLang();
  return (
    <div>
      {label && <label className="block text-xs font-semibold text-gray-400 mb-1.5">{t(label)}</label>}
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid}
        className="w-full bg-[#0f0f0f] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-[#F2BC2E] focus:outline-none transition-colors" />
    </div>
  );
}

export function Select({ label, value, onChange, options, testid }) {
  const { t } = useLang();
  return (
    <div>
      {label && <label className="block text-xs font-semibold text-gray-400 mb-1.5">{t(label)}</label>}
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0f0f0f] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F2BC2E] focus:outline-none transition-colors">
        {options.map((o) => <option key={o.value} value={o.value} className="bg-[#0f0f0f]">{t(o.label)}</option>)}
      </select>
    </div>
  );
}
